import Checkbox from '@ingka/checkbox';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';
import type { DtoOrderOrderTypeEnum } from '@repo/api-types/order-retrieval';
import { translate } from '@repo/phrase';
import { type AssetType, assetTypes } from '@repo/types/asset';

import type { ShareOption } from '@/components/share/share-options';

import { getAppConfig } from '@/utils/window.utils.ts';

import styles from './styles.module.scss';

interface Props {
    assetType: AssetType;
    orderType: DtoOrderOrderTypeEnum | undefined;
    sms: ShareOption;
    setSms: (sms: ShareOption) => void;
    shouldValidate: boolean;
}

export const SmsShareOption = ({ sms, setSms, shouldValidate, assetType, orderType }: Props) => {
    const config = getAppConfig();

    const smsConfig = config.shareOptions.smsEnabledFor;

    // Do not render the SMS share option if:
    // 1. SMS sharing is not enabled for the asset type
    // 2. SMS sharing is enabled for the order, but not for the specific order type
    if (
        !smsConfig.assetTypes.includes(assetType) ||
        (assetType === assetTypes.order && orderType && !smsConfig.orderTypes.includes(orderType))
    ) {
        return null;
    }

    return (
        <div className={styles.form_group} data-testid="sms-share-option">
            <Checkbox
                id="sms-check"
                data-testid="sms-check"
                name={translate('share_option_sms')}
                aria-label={translate('share_option_sms')}
                value="sms"
                subtle
                checked={sms.checked}
                onChange={() => {
                    setSms({ ...sms, checked: !sms.checked });
                }}
            />
            <FormField
                className={String(styles.form_field)}
                shouldValidate={sms.checked && shouldValidate}
                valid={Boolean(sms.value) && sms.value !== ''}
                validation={{
                    msg: translate('form_field_msg_required_field'),
                    id: 'error',
                    type: 'error',
                }}
            >
                <InputField
                    id="smsInput"
                    type="text"
                    label={translate('share_option_sms')}
                    aria-label={translate('share_option_sms')}
                    describedById={translate('share_option_sms')}
                    value={sms.value ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSms({ ...sms, value: e.target.value });
                    }}
                    disabled={!sms.checked}
                />
            </FormField>
        </div>
    );
};
